import { useMemo } from 'react';
/* eslint-disable react-hooks/exhaustive-deps */
export default function useEventDetailsPermissions(context) {
  const {
    authUser,
    resolveAdminPermission,
    resolveEventDetailPermission,
    sessionPermissionsHydrated,
    sessionPermissionsOverride,
    sessionRoleOverride,
  } = context;
    const adminRoleKey = useMemo(
      () => String(sessionRoleOverride || authUser?.role || '').trim().toLowerCase() || 'general',
      [authUser?.role, sessionRoleOverride]
    );
    const isTaskOnlyRole = adminRoleKey === 'task_only';
    const adminPermissionList = useMemo(() => {
      if (isTaskOnlyRole && !sessionPermissionsHydrated) {
        return [];
      }
      if (Array.isArray(sessionPermissionsOverride)) {
        return sessionPermissionsOverride;
      }
      if (Array.isArray(authUser?.permissions)) {
        return authUser.permissions;
      }
      if (authUser?.permissions) {
        return [authUser.permissions];
      }
      return [];
    }, [authUser?.permissions, isTaskOnlyRole, sessionPermissionsHydrated, sessionPermissionsOverride]);
    const canViewCastingSettings = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_VIEW_CASTING_SETTINGS',
        }),
      [adminPermissionList, adminRoleKey]
    );
    const canViewParticipants = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_VIEW_PARTICIPANTS',
        }),
      [adminPermissionList, adminRoleKey]
    );
    const canViewTicketSales = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_VIEW_TICKET_SALES',
        }),
      [adminPermissionList, adminRoleKey]
    );
    const canViewGuestTickets = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_VIEW_GUEST_TICKETS',
        }),
      [adminPermissionList, adminRoleKey]
    );
    const canViewEventTasks = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_VIEW_EVENT_TASKS',
        }),
      [adminPermissionList, adminRoleKey]
    );
    const canViewTimetable = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_VIEW_TIMETABLE',
        }),
      [adminPermissionList, adminRoleKey]
    );
    const canViewMaterials = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_VIEW_MATERIALS',
        }),
      [adminPermissionList, adminRoleKey]
    );
    const canDownloadMaterials = useMemo(
      () =>
        canViewMaterials &&
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_DOWNLOAD_MATERIALS',
        }),
      [adminPermissionList, adminRoleKey, canViewMaterials]
    );
    const canViewEventImages = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_VIEW_IMAGES',
        }),
      [adminPermissionList, adminRoleKey]
    );
    const canDownloadEventImages = useMemo(
      () =>
        canViewEventImages &&
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_DOWNLOAD_IMAGES',
        }),
      [adminPermissionList, adminRoleKey, canViewEventImages]
    );
    const canViewContractStatus = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_VIEW_CONTRACT_STATUS',
        }),
      [adminPermissionList, adminRoleKey]
    );
    const canUseEventChat = useMemo(
      () =>
        resolveAdminPermission({
          role: adminRoleKey,
          isExternalAccount: authUser?.is_external_account === true,
          permissions: adminPermissionList,
          permissionKey: 'NAV_EVENT_CHAT',
        }),
      [adminPermissionList, adminRoleKey, authUser?.is_external_account]
    );
    const canViewSurveys = useMemo(
      () =>
        resolveAdminPermission({
          role: adminRoleKey,
          isExternalAccount: authUser?.is_external_account === true,
          permissions: adminPermissionList,
          permissionKey: 'NAV_SURVEYS',
        }),
      [adminPermissionList, adminRoleKey, authUser?.is_external_account]
    );
    const canEditEventInfo = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_EDIT_INFO',
        }),
      [adminPermissionList, adminRoleKey]
    );
    const canEditCastingSettings = useMemo(
      () =>
        canEditEventInfo &&
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_EDIT_CASTING_SETTINGS',
        }),
      [adminPermissionList, adminRoleKey, canEditEventInfo]
    );
    const canEditParticipants = useMemo(
      () =>
        canEditEventInfo &&
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_EDIT_PARTICIPANTS',
        }),
      [adminPermissionList, adminRoleKey, canEditEventInfo]
    );
    const canEditTicketSales = useMemo(
      () =>
        canEditEventInfo &&
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_EDIT_TICKET_SALES',
        }),
      [adminPermissionList, adminRoleKey, canEditEventInfo]
    );
    const canShareTicketSales = useMemo(
      () =>
        canViewTicketSales &&
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_SHARE_TICKET_SALES',
        }),
      [adminPermissionList, adminRoleKey, canViewTicketSales]
    );
    const canEditGuestTickets = useMemo(
      () =>
        canViewGuestTickets &&
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_EDIT_GUEST_TICKETS',
        }),
      [adminPermissionList, adminRoleKey, canViewGuestTickets]
    );
    const canManageGuestTicketAllocations = useMemo(
      () =>
        canViewGuestTickets &&
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_MANAGE_GUEST_TICKET_ALLOCATIONS',
        }),
      [adminPermissionList, adminRoleKey, canViewGuestTickets]
    );
    const canEditEventTasks = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_EDIT_EVENT_TASKS',
        }),
      [adminPermissionList, adminRoleKey]
    );
    const canEditTimetable = useMemo(
      () =>
        canEditEventInfo &&
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_EDIT_TIMETABLE',
        }),
      [adminPermissionList, adminRoleKey, canEditEventInfo]
    );
    const canEditMaterials = useMemo(
      () =>
        canEditEventInfo &&
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_EDIT_MATERIALS',
        }),
      [adminPermissionList, adminRoleKey, canEditEventInfo]
    );
    const canEditEventImages = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRoleKey,
          permissions: adminPermissionList,
          permissionKey: 'EVENT_EDIT_IMAGES',
        }),
      [adminPermissionList, adminRoleKey]
    );
    const eventReadOnlyTitle = isTaskOnlyRole
      ? 'タスク管理専用ロールではイベント情報を変更できません。'
      : 'このユーザー権限ではこの操作を実行できません。';
  return {
    adminRoleKey,
    isTaskOnlyRole,
    adminPermissionList,
    canViewCastingSettings,
    canViewParticipants,
    canViewTicketSales,
    canViewGuestTickets,
    canViewEventTasks,
    canViewTimetable,
    canViewMaterials,
    canDownloadMaterials,
    canViewEventImages,
    canDownloadEventImages,
    canViewContractStatus,
    canUseEventChat,
    canViewSurveys,
    canEditEventInfo,
    canEditCastingSettings,
    canEditParticipants,
    canEditTicketSales,
    canShareTicketSales,
    canEditGuestTickets,
    canManageGuestTicketAllocations,
    canEditEventTasks,
    canEditTimetable,
    canEditMaterials,
    canEditEventImages,
    eventReadOnlyTitle,
  };
}
